import { TextInput } from '@socialchorus/shared-ui-components';
import React, {
  ComponentProps,
  ForwardedRef,
  forwardRef,
  KeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import {
  UserSuggestion,
  useUserSuggestions,
} from '../../../common/use-user-suggestions';
import { MentionMenuItem } from '../../mention-shared/mention-menu-item';

import { getCaretCoordinates } from './caret';
import { detectMentionTrigger, MentionTrigger } from './mention-trigger';
import { computeAtomicDelete, reconcile } from './reconcile';
import { build, MentionRange, parse } from './serialization';

type TextInputProps = ComponentProps<typeof TextInput>;

export type ReferenceTextInputProps = Omit<
  TextInputProps,
  'value' | 'onChange'
> & {
  /** Canonical value including full reference tokens. */
  value: string;
  /** Emits the canonical value (with reference tokens). */
  onChange: (value: string) => void;
  /** When false the component is a plain pass-through TextInput. */
  mentionsEnabled?: boolean;
  /**
   * When true, typing (and pasting) past `maxLength` is still allowed — the
   * native `maxlength` attribute is stripped from the underlying <input> so
   * the browser no longer hard-blocks further keystrokes, while the
   * character counter and over-limit error message (driven by `maxLength`)
   * keep working as usual. Defaults to false, preserving the native
   * hard-cap behavior existing/future consumers of `maxLength` expect.
   */
  allowExceedingMaxLength?: boolean;
};

interface MenuPosition {
  top: number;
  left: number;
}

function ReferenceTextInputInner(
  {
    value,
    onChange,
    mentionsEnabled = true,
    disabled,
    maxLength,
    allowExceedingMaxLength = false,
    onKeyDown,
    onSelect,
    onBlur,
    ...passthrough
  }: ReferenceTextInputProps,
  forwardedRef: ForwardedRef<HTMLInputElement>
) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const setRefs = useCallback(
    (el: HTMLInputElement | null) => {
      inputRef.current = el;
      if (typeof forwardedRef === 'function') forwardedRef(el);
      else if (forwardedRef) forwardedRef.current = el;
    },
    [forwardedRef]
  );

  // Display string + mention ranges are always derived from the canonical
  // value, so there is no separate mention state to drift.
  const { display, mentions } = useMemo(
    () => (mentionsEnabled ? parse(value) : { display: value, mentions: [] }),
    [value, mentionsEnabled]
  );

  const [trigger, setTrigger] = useState<MentionTrigger | null>(null);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const active = mentionsEnabled && !disabled && trigger !== null;
  const suggestions = useUserSuggestions(active ? trigger!.query : '');

  useEffect(() => {
    setActiveIndex(0);
  }, [suggestions]);

  // shared-ui TextInput memoizes its onChange handler at first mount, so the
  // handler we hand it must read live state through refs rather than relying on
  // per-render closures (otherwise it reconciles every edit against the initial,
  // empty value and silently drops mentions). Keep these in sync every render.
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const mentionsEnabledRef = useRef(mentionsEnabled);
  const disabledRef = useRef(disabled);
  valueRef.current = value;
  onChangeRef.current = onChange;
  mentionsEnabledRef.current = mentionsEnabled;
  disabledRef.current = disabled;

  const parseCurrent = useCallback(
    () =>
      mentionsEnabledRef.current
        ? parse(valueRef.current)
        : { display: valueRef.current, mentions: [] as MentionRange[] },
    []
  );

  // Restore the caret after a programmatic value change (insert / atomic delete),
  // which a controlled input would otherwise collapse to the end.
  const pendingCaret = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (pendingCaret.current !== null && inputRef.current) {
      const pos = pendingCaret.current;
      inputRef.current.setSelectionRange(pos, pos);
      pendingCaret.current = null;
    }
  });

  // TextInput forwards `maxLength` straight onto the native <input>, and the
  // browser enforces that attribute by silently refusing further keystrokes
  // once the limit is reached — the input "locks" with no feedback. Callers
  // that opt in via `allowExceedingMaxLength` still get `maxLength` passed to
  // TextInput for its built-in character counter and the over-limit
  // messaging below, but we strip the `maxlength` attribute off the
  // underlying DOM node so typing (and paste) can continue past the limit.
  // Other consumers keep the native hard cap, since that's the behavior the
  // inherited `maxLength` prop implies by default.
  useLayoutEffect(() => {
    if (allowExceedingMaxLength) {
      inputRef.current?.removeAttribute('maxlength');
    }
  });

  // The native `tooLong` validity (and therefore TextInput's built-in message)
  // only flags values the user typed, never a programmatically inserted mention,
  // and shared-ui clears any setCustomValidity we apply. Drive TextInput's own
  // error span through `customErrorMessage` instead — it is plain React state,
  // so it shows/clears immediately on insert and delete. The wording mirrors
  // the browser's native over-limit message (see FE-32494).
  const overLimit = typeof maxLength === 'number' && display.length > maxLength;
  const overLimitMessage = overLimit
    ? `Please shorten this text to ${maxLength} characters or less (you are currently using ${display.length} characters).`
    : undefined;

  const closeMenu = useCallback(() => {
    setTrigger(null);
    setMenuPosition(null);
  }, []);

  const refreshTrigger = useCallback(() => {
    const input = inputRef.current;
    if (!input || !mentionsEnabledRef.current || disabledRef.current) {
      closeMenu();
      return;
    }
    // Only while there is a collapsed caret (not an active range selection).
    if (input.selectionStart !== input.selectionEnd) {
      closeMenu();
      return;
    }
    const caret = input.selectionStart ?? 0;
    // Pass live mention ranges so the caret sitting inside/at the end of an
    // existing token does not re-open the picker on the token's own `@Name`.
    const { mentions: liveMentions } = parseCurrent();
    const next = detectMentionTrigger(input.value, caret, liveMentions);
    if (!next) {
      closeMenu();
      return;
    }
    const coords = getCaretCoordinates(input, next.start);
    const rect = input.getBoundingClientRect();
    setTrigger(next);
    setMenuPosition({
      top: rect.top + coords.top + coords.height,
      left: rect.left + coords.left - input.scrollLeft,
    });
  }, [closeMenu, parseCurrent]);

  const handleChange = useCallback(
    (nextDisplay: string) => {
      const current = parseCurrent();
      const result = reconcile(current.display, current.mentions, nextDisplay);
      const nextValue = build(result.display, result.mentions);
      // Advance the ref synchronously so refreshTrigger's parseCurrent() sees
      // the post-edit mention ranges — React state (and therefore the `value`
      // prop that normally updates valueRef on render) has not propagated yet.
      // Without this, an edit that shifts or demotes a mention would leave
      // refreshTrigger comparing the fresh caret against stale ranges.
      valueRef.current = nextValue;
      onChangeRef.current(nextValue);
      // The DOM input already holds nextDisplay with an updated caret, so read
      // the trigger straight from it.
      refreshTrigger();
    },
    [parseCurrent, refreshTrigger]
  );

  const selectSuggestion = useCallback(
    (suggestion: UserSuggestion) => {
      if (!trigger) return;
      const insertion = `@${suggestion.name}`;
      // Insert a trailing plain space after the token; the mention range itself
      // excludes the space, and the caret lands after it.
      const withSpace = `${insertion} `;
      const before = display.slice(0, trigger.start);
      const after = display.slice(trigger.end);
      const nextDisplay = before + withSpace + after;

      const delta = withSpace.length - (trigger.end - trigger.start);
      const newMention: MentionRange = {
        id: String(suggestion.id),
        name: suggestion.name,
        refType: 'user',
        start: trigger.start,
        end: trigger.start + insertion.length,
      };
      const nextMentions = mentions
        // Keep mentions outside the replaced `@query` range; shift trailing ones.
        .filter((m) => m.end <= trigger.start || m.start >= trigger.end)
        .map((m) =>
          m.start >= trigger.end
            ? { ...m, start: m.start + delta, end: m.end + delta }
            : { ...m }
        )
        .concat(newMention)
        .sort((a, b) => a.start - b.start);

      pendingCaret.current = trigger.start + withSpace.length;
      onChange(build(nextDisplay, nextMentions));
      closeMenu();
    },
    [trigger, display, mentions, onChange, closeMenu]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(e);
      if (e.defaultPrevented) return;
      const input = inputRef.current;
      if (!input) return;

      if (active && suggestions.length > 0) {
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            setActiveIndex((i) => (i + 1) % suggestions.length);
            return;
          case 'ArrowUp':
            e.preventDefault();
            setActiveIndex(
              (i) => (i - 1 + suggestions.length) % suggestions.length
            );
            return;
          case 'Enter':
          case 'Tab':
            e.preventDefault();
            selectSuggestion(suggestions[activeIndex]);
            return;
          case 'Escape':
            e.preventDefault();
            closeMenu();
            return;
          default:
            break;
        }
      }

      if (e.key === 'Backspace' || e.key === 'Delete') {
        const result = computeAtomicDelete(
          display,
          mentions,
          input.selectionStart ?? 0,
          input.selectionEnd ?? 0,
          e.key === 'Backspace' ? 'backward' : 'forward'
        );
        if (result) {
          e.preventDefault();
          pendingCaret.current = result.caret;
          onChange(build(result.display, result.mentions));
          closeMenu();
        }
      }
    },
    [
      onKeyDown,
      active,
      suggestions,
      activeIndex,
      selectSuggestion,
      closeMenu,
      display,
      mentions,
      onChange,
    ]
  );

  const handleSelect = useCallback(
    (e: React.SyntheticEvent<HTMLInputElement>) => {
      onSelect?.(e);
      refreshTrigger();
    },
    [onSelect, refreshTrigger]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(e);
      closeMenu();
    },
    [onBlur, closeMenu]
  );

  const showMenu = active && suggestions.length > 0 && menuPosition !== null;

  // TextInput's props are a discriminated union (label / character-counter
  // variants); merging the spread with our handlers and casting once keeps the
  // union from collapsing under a JSX spread.
  const textInputProps = {
    ...passthrough,
    disabled,
    maxLength,
    value: display,
    customErrorMessage: overLimitMessage,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    onSelect: handleSelect,
    onBlur: handleBlur,
  } as TextInputProps;

  return (
    <>
      <TextInput {...textInputProps} ref={setRefs} />
      {showMenu &&
        createPortal(
          // Keep the input focused when an option is clicked so onClick fires
          // before the blur-driven close.
          <div
            className="lexical-mention-dropdown"
            role="listbox"
            aria-label="User suggestions"
            tabIndex={-1}
            style={{
              position: 'fixed',
              top: menuPosition!.top,
              left: menuPosition!.left,
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            {suggestions.map((suggestion, index) => (
              <MentionMenuItem
                key={suggestion.id}
                data={{
                  id: String(suggestion.id),
                  name: suggestion.name,
                  avatar: suggestion.avatar,
                  displayAttributes: suggestion.display_attributes,
                }}
                isSelected={index === activeIndex}
                onSelect={() => selectSuggestion(suggestion)}
                onHover={() => setActiveIndex(index)}
              />
            ))}
          </div>,
          document.body
        )}
    </>
  );
}

export const ReferenceTextInput = forwardRef(ReferenceTextInputInner);
